import { Button } from '@heidi/design-system/button';
import { FramedIcon } from '@heidi/design-system/framed-icon';
import { Link } from '@heidi/design-system/link';
import {
  DialogDrawer,
  DialogDrawerContent,
} from '@heidi/design-system/mobile-responsive-dialog-drawer';
import {
  TypographyCaption,
  TypographyH2,
  TypographyP2,
  TypographyP2Bold,
} from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { useAtom } from 'jotai';
import {
  ExternalLinkIcon,
  Gauge,
  NotebookPen,
  PlugZap,
  Sparkles,
  Users2,
} from 'lucide-react';
import { FormattedMessage } from 'react-intl';

import { stripeSuccessModalOpenAtom } from '../../../data/atoms/stripeSuccessModalAtom';

export const HeidiProUpgradedSuccessful = () => {
  const [stripeSuccessModalOpen, setStripeSuccessModalOpen] = useAtom(
    stripeSuccessModalOpenAtom
  );

  const handleStartUsingPro = () => {
    setStripeSuccessModalOpen(false);
  };

  return (
    <DialogDrawer open={stripeSuccessModalOpen} onOpenChange={setStripeSuccessModalOpen}>
      <DialogDrawerContent
        className={cn(
          'flex flex-col gap-8 p-4 sm:p-8',
          'h-[90dvh] sm:h-fit',
          'max-h-fit',
          'w-full sm:w-[500px]'
        )}
        hideCloseIcon
      >
        <div className="flex flex-col items-center gap-4 text-center">
          <FramedIcon state="pro" />
          <TypographyH2>
            <FormattedMessage
              defaultMessage="You're officially a Heidi Pro!"
              id="hf+mueAn27"
              description="Title of the modal shown after upgrading to Heidi Pro"
            />
          </TypographyH2>
        </div>

        <div className="flex flex-col gap-3">
          <TypographyP2Bold className="text-text-secondary">
            <FormattedMessage
              defaultMessage="You've unlocked access to:"
              id="wRlCNsWFot"
              description="Lead-in above the list of Heidi Pro features"
            />
          </TypographyP2Bold>
          <ul className="space-y-2">
            <li className="flex items-center gap-3">
              <NotebookPen className="text-tier-pro-text" width={24} height={24} />
              <div>
                <TypographyP2>
                  <FormattedMessage
                    defaultMessage="Notes that sound even more like you"
                    id="rQkz82QBMc"
                    description="Heidi Pro feature headline"
                  />
                </TypographyP2>
                <TypographyCaption className="text-text-secondary">
                  <FormattedMessage
                    defaultMessage="With unlimited use of custom templates"
                    id="5R2YSvNDCD"
                    description="Heidi Pro feature detail"
                  />
                </TypographyCaption>
              </div>
            </li>

            <li className="flex items-center gap-2">
              <Sparkles className="text-tier-pro-text" width={24} height={24} />
              <div>
                <TypographyP2>
                  <FormattedMessage
                    defaultMessage="AI-driven edits and something"
                    id="TwnIVzSHlb"
                    description="Heidi Pro feature headline"
                  />
                </TypographyP2>
                <TypographyCaption className="text-text-secondary">
                  <FormattedMessage
                    defaultMessage="With unlimited use of Ask Heidi"
                    id="j8pt4PnVpk"
                    description="Heidi Pro feature detail"
                  />
                </TypographyCaption>
              </div>
            </li>

            <li className="flex items-center gap-2">
              <Users2 className="text-tier-pro-text" width={24} height={24} />
              <div>
                <TypographyP2>
                  <FormattedMessage
                    defaultMessage="Team collaboration"
                    id="Z5L+XmvN9i"
                    description="Heidi Pro feature headline"
                  />
                </TypographyP2>
                <TypographyCaption className="text-text-secondary">
                  <FormattedMessage
                    defaultMessage="Share templates, sessions and billing with your team"
                    id="qGcN8AfFf7"
                    description="Heidi Pro feature detail"
                  />
                </TypographyCaption>
              </div>
            </li>

            <li className="flex items-center gap-2">
              <PlugZap className="text-tier-pro-text" width={24} height={24} />
              <div>
                <TypographyP2>
                  <FormattedMessage
                    defaultMessage="Seamless workflow with your existing software"
                    id="Nl/fWgN/Sy"
                    description="Heidi Pro feature headline"
                  />
                </TypographyP2>
                <TypographyCaption className="text-text-secondary">
                  <FormattedMessage
                    defaultMessage="With EMR integrations"
                    id="S6AUwLTIEX"
                    description="Heidi Pro feature detail"
                  />
                </TypographyCaption>
              </div>
            </li>

            <li className="flex items-center gap-2">
              <Gauge className="text-tier-pro-text" width={24} height={24} />
              <div>
                <TypographyP2>
                  <FormattedMessage
                    defaultMessage="Light speed productivity"
                    id="cDeLm6TM/J"
                    description="Heidi Pro feature headline"
                  />
                </TypographyP2>
                <TypographyCaption className="text-text-secondary">
                  <FormattedMessage
                    defaultMessage="With priority processing speed"
                    id="MHkSLtM9XY"
                    description="Heidi Pro feature detail"
                  />
                </TypographyCaption>
              </div>
            </li>
          </ul>
        </div>

        <div className="flex flex-col gap-4">
          <Button size="lg" onClick={handleStartUsingPro} className="w-full">
            <FormattedMessage
              defaultMessage="Start using Heidi Pro"
              id="apepNL3Uyw"
              description="Button closing the upgrade success modal"
            />
          </Button>
          <Link
            href="https://www.heidihealth.com/pricing"
            target="_blank"
            size="sm"
            className="self-center"
          >
            <FormattedMessage
              defaultMessage="Learn more about Heidi Pro"
              id="796GsI/PeM"
              description="Link to the Heidi Pro pricing page"
            />
            <ExternalLinkIcon />
          </Link>
        </div>
      </DialogDrawerContent>
    </DialogDrawer>
  );
};
